import React, { useEffect } from 'react';
import useGameStore from '../store/gameStore';
import { Trophy, Medal, Clock, Loader2, Eye } from 'lucide-react';
import { getModelDisplayName } from '../constants/modelConstants';

const ScoresDisplay: React.FC = () => {
  const { highScores, loadHighScores, isLoadingHighScores } = useGameStore();

  // Load high scores when component mounts
  useEffect(() => {
    loadHighScores();
  }, [loadHighScores]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  // Format model name with vision indicator if enabled
  const formatModelName = (modelId: string, visionEnabled: boolean) => {
    const displayName = getModelDisplayName(modelId);
    return visionEnabled ? (
      <div className="flex items-center gap-1">
        {displayName}
        <Eye size={14} className="text-cyan-400" />
      </div>
    ) : displayName;
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="bg-black/50 border border-terminal-green p-5 rounded">
        <h2 className="font-press-start text-xl text-center mb-6 flex items-center justify-center gap-3">
          <Trophy className="text-yellow-500" size={24} />
          CLASSIFICA
          <Trophy className="text-yellow-500" size={24} />
        </h2>

        {isLoadingHighScores ? (
          <div className="font-vt323 text-xl text-center p-8 border border-dashed border-terminal-green/50 flex justify-center items-center">
            <Loader2 className="animate-spin mr-2" size={24} />
            Caricamento punteggi...
          </div>
        ) : highScores.length === 0 ? (
          <div className="font-vt323 text-xl text-center p-8 border border-dashed border-terminal-green/50">
            Nessun punteggio ancora. Gioca una partita per stabilire il tuo primo record!
          </div>
        ) : (
          <div className="font-vt323">
          <div className="grid grid-cols-9 md:grid-cols-12 gap-2 p-2 border-b border-terminal-green/30 mb-3 text-lg">
            <div className="col-span-1 font-bold">#</div>
            <div className="col-span-3 font-bold">NOME</div>
            <div className="col-span-2 font-bold text-right">PUNTEGGIO</div>
            <div className="col-span-3 font-bold">VS</div>
            <div className="hidden md:block col-span-3 font-bold">DATA</div>
          </div>
          
          {highScores
            .sort((a, b) => b.score - a.score)
            .map((score, index) => (
              <div 
                key={index} 
                className={`grid grid-cols-9 md:grid-cols-12 gap-2 p-2 ${index % 2 === 0 ? 'bg-black/30' : ''} hover:bg-terminal-green/20 transition-colors text-lg`}
              >
                <div className="col-span-1 flex items-center">
                  {index === 0 ? (
                    <Trophy size={18} className="text-yellow-500" />
                  ) : index === 1 ? (
                    <Medal size={18} className="text-gray-300" />
                  ) : index === 2 ? (
                    <Medal size={18} className="text-amber-600" />
                  ) : (
                    <span>{index + 1}</span>
                  )}
                </div>
                <div className="col-span-3 truncate">{score.name}</div>
                <div className="col-span-2 text-right font-bold text-yellow-500">{score.score}</div>
                <div className="col-span-3 truncate">
                  {formatModelName(score.model, score.visionEnabled)}
                </div>
                <div className="hidden md:flex col-span-3 items-center gap-1 text-sm">
                  <Clock size={14} />
                  {formatDate(score.date)}
                </div>
              </div>
            ))}
        </div>
        )}

        <div className="mt-6 p-4 border border-terminal-green/30 bg-black/30 font-vt323">
          <h3 className="text-lg mb-2 text-terminal-green">Come vengono calcolati i punteggi:</h3>
          <ul className="list-disc list-inside space-y-1 text-terminal-green/70">
            <li>Bonus Vittoria: 500 punti</li>
            <li>Moltiplicatore Tempo: bonus proporzionale ai secondi rimanenti</li>
            <li>Moltiplicatore Modello (bonus difficoltà)</li>
            <li>Le vittorie con il Nero ricevono un bonus del 4%</li>
            <li>Vinci più velocemente contro modelli più forti per il punteggio più alto!</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ScoresDisplay;